import { create } from '@thoughtspot/logger';
import {
    MetricsService,
    UserAction,
    userActionTypeEnum,
} from '@thoughtspot/metrics';
import {
    PqlCaptureEventId,
    sendOnboardingEvent,
} from '@thoughtspot/onboarding-event-service';
import {
    PhraseType,
    SagePhrase,
    SageToken,
} from '/@services/generated/graphql-types';
import {
    createNewEmptyPhrase,
    createNewEmptyToken,
} from '/@services/search/search-service.util';
import {
    BlinkCompletionOrFoldedCompletion,
    BlinkCompletionType,
    countTokensInPhrases,
    getClassificationForPhrase,
    getPhraseAndTokenIdxForAbsoluteTokenIdx,
    getTextForTokens,
    getTooltipForSagePhrase,
    isCompletionACohort,
    isCompletionFromACohort,
    PhraseClassification,
} from '/@utils/sage.utils';
import { matchTextToTokens } from '/@utils/tokenization.util';
import { TokenizerToken } from '../../token-bar/tokenizer/tokenizer.config';
import { SelectedCompletion } from './sage-completions-dropdown/sage-completions-dropdown';
import styles from './sage-search-box.module.scss';

/*
 * Represents the absolute offset of the caret in the set of tokens as a flattened string, and also
 * accounts for the difference between being inside a separator, or being at the edge of a token
 * but not in the separator. If the caret is inside a separator, the caret offset is recorded as
 * being at the end of the previous token.
 *
 * For example, if the token text is "rev date" (2 tokens):
 * - if the caret is at the end of "rev" and inside that token, the representation would be:
 *   { caretCharIdx: 3, isCaretInSeparator: false }
 * - if the caret is in the separator between "rev" and "date", the representation would be:
 *   { caretCharIdx: 3, isCaretInSeparator: true }
 * - if the caret is at the start of "date" and inside that token, the representation would be:
 *   { caretCharIdx: 4, isCaretInSeparator: false }
 */
export interface CaretPosition {
    caretCharIdx: number;
    isCaretInSeparator: boolean;
}

export enum QueryFiredActionSource {
    ENTER = 'ENTER',
    CLICK_GO = 'CLICK_GO',
    CLICK_SAGE_CURTAIN = 'CLICK_SAGE_CURTAIN',
    ESCAPE = 'ESCAPE',
}

const CSS_CLASS_NAMES_BY_PHRASE_CLASSIFICATION: {
    [key in PhraseClassification]: string;
} = {
    [PhraseClassification.Unrecognized]: styles.unrecognizedToken,
    [PhraseClassification.Measure]: styles.measureToken,
    [PhraseClassification.Attribute]: styles.attributeToken,
    [PhraseClassification.Date]: styles.dateBucketToken,
    [PhraseClassification.Other]: styles.otherToken,
};

const logger = create('Sage-Search-Box.Util');

export function getCssClassForPhrase(phrase: SagePhrase): string {
    if (phrase.isTypePending) {
        return styles.pendingToken;
    }

    if (!phrase.isComplete || phrase.type === PhraseType.UndefinedPhrase) {
        return styles.unrecognizedToken;
    }

    // We select the first token in sage ranking in case of disambiguation
    // when search assist is enabled, though we give user an option to
    // choose when it is a single token phrase [SCAL-83524]
    if (phrase.isAmbiguous) {
        return styles.ambiguousToken;
    }

    return CSS_CLASS_NAMES_BY_PHRASE_CLASSIFICATION[
        getClassificationForPhrase(phrase)
    ];
}

export function convertPhrasesToTokenBarTokens(
    phrases: SagePhrase[],
    phraseIds: string[],
): TokenizerToken[] {
    return phrases
        .map((phrase: SagePhrase, idx: number) => {
            const tokenText = phrase.tokens
                .map((token: SageToken, idx: number) => {
                    if (idx === phrase.tokens.length - 1) {
                        return token.text;
                    }
                    return token.text + (token.hasSpaceAfter ? ' ' : '');
                })
                .join('');

            return {
                id: phraseIds[idx],
                text: tokenText,
                // Tooltip may be undefined if we just selected a completion and we optimistically
                // updated the phrases state. We will compute the tooltip upon getting a sage
                // response, but until then it will not be defined. [SCAL-88458]
                tooltip: getTooltipForSagePhrase(phrase),
                cssClassNames: getCssClassForPhrase(phrase),
                isIncomplete: !phrase.isComplete,
                isExtensible: !!phrase.tokens[phrase.tokens.length - 1]
                    .isExtensible,
            };
        })
        .filter(
            // Do not display an empty token
            (token: TokenizerToken) => token.text.length > 0,
        );
}

/*
 * Since we transformed the sage phrases/tokens into token bar tokens, we have to also transform
 * a token index in the original set of sage phrases/tokens into a token bar token index + char
 * offset within that token. For example, "color = red" would be a single sage phrase consisting of
 * 3 tokens, but it would be transformed into a single token bar token. If we wanted to refer to
 * the position at the end of the second sage token ("="), we need to transform the original sage
 * token index of 1 into the token bar token index of 0, with a char offset of 7.
 */
export function getTokenBarTokenIdxAndOffsetForSageTokenIdx(
    phrases: SagePhrase[],
    targetSageTokenIdx: number,
) {
    const {
        phraseIdx,
        tokenIdx: sageTokenIdx,
    } = getPhraseAndTokenIdxForAbsoluteTokenIdx(phrases, targetSageTokenIdx);
    const targetPhraseTokens = phrases[phraseIdx].tokens;

    let charOffsetInToken = 0;
    for (let tokenIdx = 0; tokenIdx < targetPhraseTokens.length; tokenIdx++) {
        const currentToken = targetPhraseTokens[tokenIdx];
        charOffsetInToken += currentToken.text.length;

        if (tokenIdx === sageTokenIdx) {
            break;
        }

        charOffsetInToken += currentToken.hasSpaceAfter ? 1 : 0;
    }

    return {
        tokenIdx: phraseIdx,
        charOffsetInToken,
    };
}

// Sage backend does not support trailing spaces at the end of tokens as part of the token text, so
// we break them up into individual empty tokens with hasSpaceAfter = true to represent each space
function splitTrailingSpacesIntoSeparateTokens(
    tokens: SageToken[],
): SageToken[] {
    const singleSpaceTokens: SageToken[] = [];
    tokens.forEach((token: SageToken) => {
        if (!token.text.endsWith(' ')) {
            singleSpaceTokens.push(token);
            return;
        }

        const tokenTextNoTrailingWhitespace = token.text.trimRight();
        singleSpaceTokens.push(
            {
                ...token,
                text: tokenTextNoTrailingWhitespace,
            },
            ...token.text
                .substring(tokenTextNoTrailingWhitespace.length)
                .split('')
                .map(() => ({
                    ...createNewEmptyToken(),
                    hasSpaceAfter: true,
                })),
        );
    });

    return singleSpaceTokens;
}

export function getTransformedTokensUsingInputText(
    oldPhrases: SagePhrase[],
    inputText: string,
    caretPosition?: CaretPosition,
) {
    // Get the flat list of tokens out of all the phrases
    const oldTokens: SageToken[] = oldPhrases.flatMap(phrase => phrase.tokens);

    // Try to match the old tokens with the new text and create new tokens for unrecognized text
    let newTokens: SageToken[] = matchTextToTokens(inputText, oldTokens, false);

    // Fill in any extra fields for any new tokens which got created during matching
    newTokens.forEach((token: SageToken) => {
        if (!token.recognizedTokenJson) {
            // eslint-disable-next-line no-param-reassign
            token.recognizedTokenJson = null;
        }
    });

    // TODO(Rifdhan) can we get sage to support this and/or do it themselves?
    newTokens = splitTrailingSpacesIntoSeparateTokens(newTokens);

    // If no caret position information was provided, don't do any special logic around the caret
    // position, instead just return dummy values for the caret information.
    if (!caretPosition) {
        // If no tokens were found, create a new empty token
        if (newTokens.length === 0) {
            newTokens = [createNewEmptyToken()];
        }

        return {
            newFlatTokens: newTokens,
            caretTokenIdx: 0,
            charOffsetInCaretToken: 0,
        };
    }

    // Find the index of the token containing the caret, and the char offset of the caret within
    // that token. Note that if the caret is inside the whitespace after a token, that token is
    // considered to contain the caret.
    let traversedCharCount = 0;
    let caretTokenIdx = -1;
    let charOffsetInCaretToken = 0;
    for (let i = 0; i < newTokens.length; i++) {
        const currentToken = newTokens[i];

        // If the current token has a space at the end of it, and the caret is positioned after
        // that space, only count the caret as being inside the current token if the current token
        // is the last token. Otherwise, we consider the caret to be located at the start of the
        // next token, and not inside the current token.
        const currentTokenLength =
            currentToken.text.length +
            (i === newTokens.length - 1 && currentToken.hasSpaceAfter ? 1 : 0);
        if (
            traversedCharCount + currentTokenLength >=
            caretPosition.caretCharIdx
        ) {
            caretTokenIdx = i;
            charOffsetInCaretToken =
                caretPosition.caretCharIdx - traversedCharCount;
            break;
        } else {
            // Always count the trailing whitespace since we always traverse it
            traversedCharCount +=
                currentToken.text.length + (currentToken.hasSpaceAfter ? 1 : 0);
        }
    }

    // TODO(Rifdhan) can remove after some testing/bake period to ensure sanity
    if (
        caretTokenIdx < 0 &&
        (newTokens.length > 0 || !caretPosition.isCaretInSeparator)
    ) {
        logger.warn(
            'Unable to find token containing caret while processing input change',
            `"${inputText}"`,
            caretPosition,
            oldPhrases,
            newTokens,
        );
        caretTokenIdx = 0;
    }

    // We create a new empty token at the caret position in either of these scenarios:
    // a) The caret is in an empty separator and outside of any token
    // b) The caret is inside whitespace at the end of a token (must have at least one whitespace
    //    character to the left of the caret position)
    // Note that (a) will be the case when there are no tokens in the search bar, in which case we
    // will be creating the first and only token during this step.
    const caretToken: SageToken | undefined = newTokens[caretTokenIdx];
    const shouldCreateNewTokenAfterCaretToken =
        caretPosition.isCaretInSeparator ||
        (caretToken &&
            caretToken.hasSpaceAfter &&
            charOffsetInCaretToken === caretToken.text.length + 1);
    if (shouldCreateNewTokenAfterCaretToken) {
        // We are adding a new empty token so that we can get completions for the next token the
        // user will create. To ensure that next token doesn't get merged onto the previous token,
        // ensure the previous token has a space after it.
        if (caretToken) {
            caretToken.hasSpaceAfter = true;
        }

        // Add the new empty token after the detected caret token, and make it the caret token
        caretTokenIdx++;
        charOffsetInCaretToken = 0;
        newTokens.splice(caretTokenIdx, 0, {
            ...createNewEmptyToken(),
            hasSpaceAfter: true,
        });
    }

    // If no tokens were found, create a new empty token
    if (newTokens.length === 0) {
        newTokens = [createNewEmptyToken()];
    }

    return {
        newFlatTokens: newTokens,
        caretTokenIdx,
        charOffsetInCaretToken,
    };
}

/*
 * Given a list of phrases, extracts the first nPrefixTokens tokens from the start of the phrases,
 * while preserving the phrase structure (the extracted tokens will still be encapsulated in their
 * original phrases)
 */
function getPrefixTokenData(phrases: SagePhrase[], nPrefixTokens: number) {
    const prefixPhrases: SagePhrase[] = [];
    let lastPrefixPhraseIdx = -1;
    if (nPrefixTokens === 0) {
        return {
            prefixPhrases,
            lastPrefixPhraseIdx,
        };
    }

    // Iterate through the tokens from left to right and add the number of tokens indicated by
    // nPrefixTokens, preserving their phrase structure
    let nPrefixTokensIncluded = 0;
    for (let phraseIdx = 0; phraseIdx < phrases.length; phraseIdx++) {
        const currentPhrase = phrases[phraseIdx];
        lastPrefixPhraseIdx = phraseIdx;
        prefixPhrases.push({
            ...currentPhrase,
            tokens: [],
        });

        for (
            let tokenIdx = 0;
            tokenIdx < phrases[phraseIdx].tokens.length;
            tokenIdx++
        ) {
            const currentToken = currentPhrase.tokens[tokenIdx];
            prefixPhrases[prefixPhrases.length - 1].tokens.push(currentToken);
            nPrefixTokensIncluded++;

            if (nPrefixTokensIncluded >= nPrefixTokens) {
                return {
                    prefixPhrases,
                    lastPrefixPhraseIdx,
                };
            }
        }
    }

    // nPrefixTokens should be less than or equal to the number of tokens in all phrases, so we
    // should have returned while running the loop above. This return return is provided as a
    // fail-safe, and to have consistent return typing for the function.
    logger.warn(
        'Ran out of tokens while building list of prefix phrases',
        phrases,
        nPrefixTokens,
    );
    return {
        prefixPhrases,
        lastPrefixPhraseIdx,
    };
}

/*
 * Given a list of phrases, extracts the first nSuffixTokens tokens from the end of the phrases,
 * while preserving the phrase structure (the extracted tokens will still be encapsulated in their
 * original phrases)
 */
function getSuffixTokenData(phrases: SagePhrase[], nSuffixTokens: number) {
    const suffixPhrases: SagePhrase[] = [];
    let firstSuffixPhraseIdx = phrases.length;
    if (nSuffixTokens === 0) {
        return {
            suffixPhrases,
            firstSuffixPhraseIdx,
        };
    }

    // Iterate through the tokens from right to left and add the number of tokens indicated by
    // nSuffixTokens, preserving their phrase structure
    let nSuffixTokensIncluded = 0;
    for (let phraseIdx = phrases.length - 1; phraseIdx >= 0; phraseIdx--) {
        if (nSuffixTokensIncluded >= nSuffixTokens) {
            break;
        }

        const currentPhrase = phrases[phraseIdx];
        firstSuffixPhraseIdx = phraseIdx;
        suffixPhrases.unshift({
            ...currentPhrase,
            tokens: [],
        });

        for (
            let tokenIdx = phrases[phraseIdx].tokens.length - 1;
            tokenIdx >= 0;
            tokenIdx--
        ) {
            const currentToken = currentPhrase.tokens[tokenIdx];
            suffixPhrases[0].tokens.unshift(currentToken);
            nSuffixTokensIncluded++;

            if (nSuffixTokensIncluded >= nSuffixTokens) {
                return {
                    suffixPhrases,
                    firstSuffixPhraseIdx,
                };
            }
        }
    }

    // nSuffixTokens should be less than or equal to the number of tokens in all phrases, so we
    // should have returned while running the loop above. This return return is provided as a
    // fail-safe, and to have consistent return typing for the function.
    logger.warn(
        'Ran out of tokens while building list of suffix phrases',
        phrases,
        nSuffixTokens,
    );
    return {
        suffixPhrases,
        firstSuffixPhraseIdx,
    };
}

/*
 * Performs two actions on the given phrases list:
 * 1. Sets hasSpaceAfter to true for the last token of the last phrase
 * 2. Adds a new empty token at the end of the token list for the last phrase
 */
function appendNewEmptyTokenToLastPhraseAndSeparateWithSpace(
    phrases: SagePhrase[],
) {
    const lastPhrase = phrases[phrases.length - 1];
    const lastPhraseToken = lastPhrase.tokens[lastPhrase.tokens.length - 1];

    // We are adding a new empty token so that we can get completions for the next token the user
    // will create. To ensure that next token doesn't get merged onto the previous token, ensure
    // the previous token has a space after it.
    lastPhrase.tokens[lastPhrase.tokens.length - 1] = {
        ...lastPhraseToken,
        hasSpaceAfter: true,
    };

    // Add the new empty token at the end of the last phrase's tokens
    lastPhrase.tokens.push(createNewEmptyToken());
}

/*
 * Handles the scenario of selecting a completion where the completion tokens replace one or more
 * tokens in the original query (eg changing from "color = red" to "color = blue"). This is a
 * helper function for getTransformedPhrasesUsingCompletion further below.
 *
 * We assume that, when selecting a completion that replaces tokens, the new tokens from the
 * completion will remain part of the same phrase as the tokens being replaced. As such, there
 * should not be any changes to the overall phrase structure of the query. It is important to
 * preserve the phrase structure to avoid jumpiness issues where selecting a completion results in
 * the wrong optimistic phrase state, which then suddenly changes into the correct state upon
 * receiving the sage response.
 */
function handleReplacementCompletionCase(
    oldPhrases: SagePhrase[],
    completion: BlinkCompletionOrFoldedCompletion,
    prefixPhrases: SagePhrase[],
    suffixPhrases: SagePhrase[],
    lastPrefixPhraseIdx: number,
    firstSuffixPhraseIdx: number,
    isLastPrefixPhrasePartial: boolean,
    isFirstSuffixPhrasePartial: boolean,
) {
    // The caret always ends up at the end of the last completion token, and the completion tokens
    // always go after the prefix tokens, so we can compute this field the same way regardless of
    // the 3 scenarios further below. We must compute it before potentially mutating the prefix
    // phrase list in later logic however, so we do it here.
    let resultingAbsoluteCaretTokenIdx =
        prefixPhrases
            .map(phrase => phrase.tokens.length)
            .reduce((a, b) => a + b, 0) +
        completion.completionPhrases[0].tokens.length -
        1;

    // The char offset of the caret in the caret phrase is computed slightly differently based on
    // whether the completion phrases are appended to the last prefix phrase or not. Each scenario
    // below can call this function with its circumstances in mind.
    let resultingCharOffsetInCaretPhrase;
    const getResultingCharOffsetInCaretPhrase = (
        isAppendingToLastPrefixPhrase: boolean,
    ) => {
        return (
            (isAppendingToLastPrefixPhrase
                ? getTextForTokens(
                      prefixPhrases[prefixPhrases.length - 1].tokens,
                  ).length
                : 0) +
            // We want the caret to be at the end of the last completion phrase, excluding any
            // trailing whitespace, so that it is touching the last token (ie "tax|" and not
            // "tax |"). Therefore, we trim any trailing whitespace at the end of the last
            // completion token.
            getTextForTokens(completion.completionPhrases[0].tokens).trimRight()
                .length
        );
    };

    if (isLastPrefixPhrasePartial && isFirstSuffixPhrasePartial) {
        // The last prefix phrase AND the first suffix phrase are missing tokens, so it must be
        // because we are replacing a token in the middle of a phrase (with untouched tokens to the
        // left and right) (eg "tax = 0" -> "tax > 0"). This also must mean the last prefix phrase
        // is the same phrase as the first suffix phrase, so we check that for sanity.
        if (lastPrefixPhraseIdx !== firstSuffixPhraseIdx) {
            logger.warn(
                'Selected a completion that impacts tokens in multiple phrases, this should not be possible',
                oldPhrases,
                completion,
            );
        }

        // The caret ends up at the end of the last token from the completion tokens, and the
        // completion tokens are being appended to the last prefix phrase, so compute the required
        // caret indices based on that expectation
        resultingCharOffsetInCaretPhrase = getResultingCharOffsetInCaretPhrase(
            true,
        );

        // The last prefix phrase and the first suffix phrase are the same phrase. Get the tokens
        // from both, and sandwich the completion tokens between them.
        // eslint-disable-next-line no-param-reassign
        prefixPhrases[prefixPhrases.length - 1].tokens = [
            ...prefixPhrases[prefixPhrases.length - 1].tokens,
            ...completion.completionPhrases[0].tokens,
            ...suffixPhrases[0].tokens,
        ];

        // We already accounted for all tokens from the first suffix phrase above, so remove it
        // from the suffix phrases list
        suffixPhrases.shift();
    } else if (isLastPrefixPhrasePartial) {
        // Only the last prefix phrase is missing tokens, so it must be because we are replacing
        // the last token in that phrase (eg "tax = 0" -> "tax = 1")

        // The caret ends up at the end of the last token from the completion tokens, and the
        // completion tokens are being appended to the last prefix phrase, so compute the required
        // caret indices based on that expectation
        resultingCharOffsetInCaretPhrase = getResultingCharOffsetInCaretPhrase(
            true,
        );

        // Add the completion tokens onto the last prefix phrase
        // eslint-disable-next-line no-param-reassign
        prefixPhrases[prefixPhrases.length - 1].tokens = [
            ...prefixPhrases[prefixPhrases.length - 1].tokens,
            ...completion.completionPhrases[0].tokens,
        ];
    } else if (isFirstSuffixPhrasePartial) {
        // Only the first suffix phrase is missing tokens, so it must be because we are replacing
        // the first token in that phrase (eg "tax = 0" -> "revenue = 0")

        // The caret ends up at the end of the last token from the completion tokens, and the
        // completion tokens are going to be the first tokens in their phrase, so compute the
        // required caret indices based on that expectation
        resultingCharOffsetInCaretPhrase = getResultingCharOffsetInCaretPhrase(
            false,
        );

        // Prepend the completion tokens to the start of the first suffix phrase, and move that
        // updated suffix phrase into the prefix phrase list instead (required for later
        // computations that assume the completion tokens end up in the last prefix phrase)
        prefixPhrases.push({
            ...suffixPhrases[0],
            tokens: [
                ...completion.completionPhrases[0].tokens,
                ...suffixPhrases[0].tokens,
            ],
        });
        suffixPhrases.shift();
    } else {
        logger.warn(
            'Called replacement completion special case handler when not required',
            oldPhrases,
            completion,
        );
    }

    // For the optimistic update, compute the phrase list before potentially inserting a new empty
    // token for the purpose of getting new completions
    // TODO(Rifdhan) we should be using cloneDeep() on these if we really want them to be untouched
    // by the logic further below. Right now any changes to the tokens inside the phrases also
    // impact this list, because of the shallow copy. We should evaluate if we actually needed to
    // create a copy before the changes anyway.
    const optimisticUpdateNewPhrases = [...prefixPhrases, ...suffixPhrases];
    const resultingCaretPhraseIdx = prefixPhrases.length - 1;

    const lastPrefixPhrase = prefixPhrases[prefixPhrases.length - 1];

    // Determine if the completion tokens were added at the end of the prefix phrase (ie. there
    // were no remaining tokens leftover in the phrase to the right of the completion tokens)
    const completionTokensWereInsertedAtEndOfLastPrefixPhrase =
        isLastPrefixPhrasePartial && !isFirstSuffixPhrasePartial;

    // If the completion tokens were inserted at the end of the last prefix phrase, and the
    // completion is not a folded completion, then we assumne that the user is done editing the
    // completion tokens, and wants to move on to the next token
    const shouldInsertNewEmptyToken =
        completionTokensWereInsertedAtEndOfLastPrefixPhrase &&
        completion.type !== BlinkCompletionType.Folded;

    // If we need to insert a new empty token, figure out if we should add it at the end of the
    // last prefix phrase, or insert a brand new phrase for it
    let moveCaretOutFromPhraseEnd = false;
    if (shouldInsertNewEmptyToken) {
        // If the completion completes the current phrase, then we move the caret out of the phrase
        // and into a newly-created empty phrase. Otherwise, we add a new token at the end of
        // the current phrase, and move the caret into that.
        if (completion.completesCurrentPhrase) {
            // Mark previous phrase as pending until we get final type from next sage request
            lastPrefixPhrase.type = PhraseType.UndefinedPhrase;
            lastPrefixPhrase.isTypePending = true;

            // The completion completes the phrase, so add an empty token in a new phrase to start
            // building a new phrase
            prefixPhrases.push(createNewEmptyPhrase());
            moveCaretOutFromPhraseEnd = true;
        } else {
            // The completion does not complete the current phrase, so add an empty token at the
            // end of the current phrase to keep building the phrase
            appendNewEmptyTokenToLastPhraseAndSeparateWithSpace(prefixPhrases);
        }

        // Caret moves into the newly-created empty token
        resultingAbsoluteCaretTokenIdx++;

        // If we are inserting a new empty phrase to get completions, we set this to null because
        // moveCaretOutFromPhraseEnd would take precedence. If we are inserting a new empty token
        // at the end of the edited phrase, we still set this to null, since the tokenizer will by
        // default insert the caret at the end of the caret phrase if no char offset is specified,
        // which is the outcome we want here.
        resultingCharOffsetInCaretPhrase = null;
    }

    // Compute the full set of phrases after potentially adding a new empty token to get new
    // completions at
    const newPhrases = [...prefixPhrases, ...suffixPhrases];

    return {
        newPhrases,
        resultingAbsoluteCaretTokenIdx,
        // For the optimistic update, we exclude any new empty token that may have been added. In
        // handling this token replacement special case, we never add any new empty tokens however,
        // so there is nothing extra we need to do here.
        optimisticUpdateData: {
            newPhrases: optimisticUpdateNewPhrases,
            resultingCaretPhraseIdx,
            // For clarity, only one of the two below fields should be specified
            resultingCharOffsetInCaretPhrase,
            moveCaretOutFromPhraseEnd,
        },
    };
}

/*
 * Top-level handler for selecting a completion. Given the initial set of phrases and the selected
 * completion, it returns the new set of tokens for the sage request, as well as the set of tokens
 * to optimistically apply in the sage bar while that sage request is in progress.
 *
 * Overview of logical steps:
 * 1. Get the set of prefix and suffix tokens that are NOT impacted by the completion (ie. they
 *    remain the same before and after the completion is applied).
 * 2. Check if the completion tokens replace existing tokens in the query. If so, we have a helper
 *    function to handle that case - handleReplacementCompletionCase above. We call that function
 *    and return its result, without needing to continue executing any more code in this function.
 * 3. If the completion does not replace existing tokens, then it must add brand new tokens. In
 *    this case, check how to add the new tokens into the query based on flags in the completion,
 *    and update the phrases data accordingly.
 */
export function getTransformedPhrasesUsingCompletion(
    oldPhrases: SagePhrase[],
    completion: BlinkCompletionOrFoldedCompletion,
) {
    // We take nPrefixTokens number of tokens from the start of the existing tokens
    const { prefixPhrases, lastPrefixPhraseIdx } = getPrefixTokenData(
        oldPhrases,
        completion.nPrefixTokens,
    );

    // We take nSuffixTokens number of tokens from the end of the existing tokens
    const { suffixPhrases, firstSuffixPhraseIdx } = getSuffixTokenData(
        oldPhrases,
        completion.nSuffixTokens,
    );

    // Check if the last prefix phrase and the first suffix phrase do not have all their tokens. If
    // so, it means the completion we selected must be replacing tokens in these phrases, hence we
    // are excluding some of the existing tokens (the ones being replaced).
    const isLastPrefixPhrasePartial =
        lastPrefixPhraseIdx >= 0 &&
        prefixPhrases[lastPrefixPhraseIdx].tokens.length <
            oldPhrases[lastPrefixPhraseIdx].tokens.length;
    const isFirstSuffixPhrasePartial =
        firstSuffixPhraseIdx < oldPhrases.length &&
        suffixPhrases[0].tokens.length <
            oldPhrases[firstSuffixPhraseIdx].tokens.length;

    // If we are replacing existing tokens with new tokens, we assume this cannot result in changes
    // to the phrase structure (ie. our replacement tokens will remain inside the same phrase as
    // the tokens they are replacing, and will not cause the phrase to break up or merge with other
    // phrases in any way). We have a helper function to handle that scenario.
    if (isLastPrefixPhrasePartial || isFirstSuffixPhrasePartial) {
        return handleReplacementCompletionCase(
            oldPhrases,
            completion,
            prefixPhrases,
            suffixPhrases,
            lastPrefixPhraseIdx,
            firstSuffixPhraseIdx,
            isLastPrefixPhrasePartial,
            isFirstSuffixPhrasePartial,
        );
    }

    // Check if we should add the completion tokens to the end of the previous phrase, or create a
    // new phrase out of it
    const completionPhrases: SagePhrase[] = [];
    if (completion.shouldAppendToPreviousPhrase) {
        // We should add the completion tokens to the end of the previous phrase, but do a few
        // sanity checks first
        if (prefixPhrases.length === 0) {
            logger.warn(
                'Selected a completion that should be appended to the previous phrase, but ' +
                    'there are no previous phrases',
                completion,
                oldPhrases,
            );
        } else if (completion.completionPhrases.length > 1) {
            logger.warn(
                'Selected a completion that should be appended to the previous phrase, but ' +
                    'there is more than one phrase in the completion',
                completion,
                oldPhrases,
            );
        } else {
            // Everything checks out, so append the completion tokens to the last prefix phrase
            const lastPrefixPhrase = prefixPhrases[prefixPhrases.length - 1];
            lastPrefixPhrase.tokens = [
                ...lastPrefixPhrase.tokens,
                ...completion.completionPhrases[0].tokens,
            ];
            // TODO(Rifdhan) why do we update this field? Do we need to do this in other places?
            lastPrefixPhrase.isComplete = completion.completesCurrentPhrase;

            // Mark the phrase as pending until we get the final type from the next sage request
            lastPrefixPhrase.type = PhraseType.UndefinedPhrase;
            lastPrefixPhrase.isTypePending = true;
        }
    } else {
        // We should create a new phrase with the completion tokens
        completionPhrases.push(...completion.completionPhrases);
        completionPhrases[completionPhrases.length - 1].isComplete =
            completion.completesCurrentPhrase;
    }

    // Compute the resulting location of the caret after applying the completion. This will be used
    // for an optimistic update, where we don't want to include/display any potential new empty
    // token we might insert in the next block below, so we compute these properties before moving
    // to that step.
    const resultingCaretPhraseIdx =
        prefixPhrases.length + completionPhrases.length - 1;
    const resultingCharOffsetInCaretPhrase = getTextForTokens(
        completion.shouldAppendToPreviousPhrase
            ? prefixPhrases[prefixPhrases.length - 1].tokens
            : completionPhrases[completionPhrases.length - 1].tokens,
    ).length;

    // Now that we have applied the selected completion, we want to show the user new completions
    // to continue building the next token in their query. We add a new empty token after the
    // completion to get new completions at that position. The exception is for a folded completion
    // however, since we want to first disambiguate the selection (which item inside the fold did
    // the user want?) before moving on to the next token.
    let didInsertNewTokenAtCaretTokenIdx = false;
    if (completion.type !== BlinkCompletionType.Folded) {
        if (completion.completesCurrentPhrase) {
            // The completion completes the phrase, so add an empty token in a new phrase to start
            // building a new phrase
            completionPhrases.push(createNewEmptyPhrase());
        } else {
            // The completion does not complete the current phrase, so add an empty token at the
            // end of the current phrase to keep building the phrase
            appendNewEmptyTokenToLastPhraseAndSeparateWithSpace(
                completion.shouldAppendToPreviousPhrase
                    ? prefixPhrases
                    : completionPhrases,
            );
        }
        didInsertNewTokenAtCaretTokenIdx = true;
    }

    // Construct the final set of phrases by combining the prefix and completion phrases,
    // sandwiching the completion phrases in the middle
    const newPhrases = [
        ...prefixPhrases,
        ...completionPhrases,
        ...suffixPhrases,
    ];

    // The caret ends up in the last completion token, and the completion tokens go after the
    // prefix tokens
    const resultingAbsoluteCaretTokenIdx =
        prefixPhrases
            .map(phrase => phrase.tokens.length)
            .reduce((a, b) => a + b, 0) +
        completionPhrases
            .map(phrase => phrase.tokens.length)
            .reduce((a, b) => a + b, 0) -
        1;

    return {
        newPhrases,
        resultingAbsoluteCaretTokenIdx,
        // For the optimistic update, we exclude any new empty token that may have been added
        optimisticUpdateData: {
            newPhrases: newPhrases.filter(
                (phrase: SagePhrase) =>
                    phrase.tokens.filter(
                        (token: SageToken) => token.text.length > 0,
                    ).length > 0,
            ),
            resultingCaretPhraseIdx,
            // For clarity, only one of the two below fields should be specified
            resultingCharOffsetInCaretPhrase: completion.completesCurrentPhrase
                ? null
                : resultingCharOffsetInCaretPhrase,
            moveCaretOutFromPhraseEnd: completion.completesCurrentPhrase,
        },
    };
}

export const collectQueryEditedEvent = (
    metricsService: MetricsService,
    isDeletion: boolean,
    queryLength: number,
    newCaretPosition: CaretPosition,
    getAnswerMixpanelProperties: () => any,
) => {
    const answerCommonProperties = getAnswerMixpanelProperties();

    metricsService.collectEvent(
        new UserAction(userActionTypeEnum.SAGE_BAR_QUERY_EDITED),
        {
            version: 1,
            isDeletion,
            queryLength,
            newCaretPosition: newCaretPosition.caretCharIdx,
            isCaretInSeparator: newCaretPosition.isCaretInSeparator,
            ...answerCommonProperties,
        },
    );
};

export const collectQueryFiredEvent = (
    metricsService: MetricsService,
    queryFiredActionSource: QueryFiredActionSource,
    getAnswerMixpanelProperties: () => any,
) => {
    const answerCommonProperties = getAnswerMixpanelProperties();
    metricsService.collectEvent(
        new UserAction(userActionTypeEnum.SAGE_BAR_QUERY_FIRED),
        {
            queryFiredActionSource,
            ...answerCommonProperties,
        },
    );
    sendOnboardingEvent(PqlCaptureEventId.SAGE_BAR_QUERY_FIRED);
};

export const collectRerunQueryModalOpenEvent = (
    metricsService: MetricsService,
    getAnswerMixpanelProperties: () => any,
) => {
    const answerCommonProperties = getAnswerMixpanelProperties();
    metricsService.collectEvent(
        new UserAction(userActionTypeEnum.SAGE_RERUN_QUERY_MODAL_OPEN),
        {
            ...answerCommonProperties,
        },
    );
};

export const collectRerunQueryModalConfirmEvent = (
    metricsService: MetricsService,
    getAnswerMixpanelProperties: () => any,
) => {
    const answerCommonProperties = getAnswerMixpanelProperties();
    metricsService.collectEvent(
        new UserAction(userActionTypeEnum.SAGE_RERUN_QUERY_MODAL_CONFIRM),
        {
            ...answerCommonProperties,
        },
    );
};

export const collectCompletionSelectedEvent = (
    metricsService: MetricsService,
    selection: SelectedCompletion,
    getAnswerMixpanelProperties: () => any,
) => {
    const { selectedCompletion } = selection;
    const answerCommonProperties = getAnswerMixpanelProperties();
    const properties: any = {
        version: 2,
        isShowMore: selection.didSelectShowMore,
        isCohort: isCompletionACohort(selectedCompletion),
        isFromCohort: isCompletionFromACohort(selectedCompletion),
        ...answerCommonProperties,
    };
    if (!selection.didSelectShowMore) {
        properties.completionIdx = selection.selectedCompletionIdx;
        properties.isFoldedCompletion =
            selection.selectedCompletion.type === BlinkCompletionType.Folded;
        properties.nCompletionTokens = countTokensInPhrases(
            selection.selectedCompletion.completionPhrases,
        );
        properties.completionContainsAnswerFormula = selection.selectedCompletion.completionPhrases.some(
            phrase => phrase.tokens.some(token => token.isFromFormula),
        );
        properties.completionContainsCohort = selection.selectedCompletion.completionPhrases.some(
            phrase => phrase.tokens.some(token => token.isFromCohort),
        );
        properties.completionPhraseTypes =
            selection.selectedCompletion.completionPhrases
                .map(phrase => phrase.type)
                .join(',') || 'None';
        properties.isUBR =
            selectedCompletion.type === BlinkCompletionType.Folded
                ? false
                : selectedCompletion.isHistory;
    }

    metricsService.collectEvent(
        new UserAction(userActionTypeEnum.SAGE_BAR_COMPLETION_SELECTED),
        properties,
    );
};

export const isQueryEmptyWithoutSpaces = (searchQuery: string) =>
    /^['\s]*$/.test(searchQuery);
